import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, BarChart2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { GraphConfig, BreakdownType, ChartType, GraphMode, HistoricalMetricType, DiffsBreakdownType, EventCategoryFilter, DiffsCountMode, DiffsChartMode, PeriodGranularity } from './types';
import { BREAKDOWN_METADATA, HISTORICAL_METRIC_METADATA, DIFFS_BREAKDOWN_METADATA, EVENT_CATEGORY_METADATA } from './types';
import { GitCompare } from 'lucide-react';
import { getRecentQuarters, getRecentYears } from '@/utils/chartDataTransformers';

interface GraphConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig?: GraphConfig;
  onSave: (config: GraphConfig) => void;
}

export const GraphConfigModal: React.FC<GraphConfigModalProps> = ({
  open,
  onOpenChange,
  initialConfig,
  onSave,
}) => {
  // Mode selection
  const [mode, setMode] = useState<GraphMode>('current');

  // Current mode state
  const [breakdown, setBreakdown] = useState<BreakdownType>('category');
  const [chartType, setChartType] = useState<ChartType>('pie');
  const [title, setTitle] = useState('');

  // Historical mode state
  const [metric, setMetric] = useState<HistoricalMetricType>('average_addons');
  const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(new Set());
  const [availableQuarters] = useState(() => getRecentQuarters(12));

  // Diffs mode state
  const [diffsBreakdown, setDiffsBreakdown] = useState<DiffsBreakdownType>('event_type');
  const [diffsSelectedPeriods, setDiffsSelectedPeriods] = useState<Set<string>>(new Set());
  const [diffsEventCategory, setDiffsEventCategory] = useState<EventCategoryFilter>('all');
  const [diffsRequireAllPeriods, setDiffsRequireAllPeriods] = useState<boolean>(true);
  const [diffsShiftQuarters, setDiffsShiftQuarters] = useState<boolean>(true);
  const [diffsCountMode, setDiffsCountMode] = useState<DiffsCountMode>('unique');
  const [diffsShowTrendLine, setDiffsShowTrendLine] = useState<boolean>(true);
  const [diffsChartMode, setDiffsChartMode] = useState<DiffsChartMode>('periods');
  const [diffsPeriodGranularity, setDiffsPeriodGranularity] = useState<PeriodGranularity>('quarterly');
  const [diffsGroupByCategory, setDiffsGroupByCategory] = useState<boolean>(false);

  // Available periods based on granularity
  const availableYears = React.useMemo(() => getRecentYears(5), []);
  const availableDiffsPeriods = diffsPeriodGranularity === 'yearly' ? availableYears : availableQuarters;

  // Default periods for diffs mode (last 7 quarters for quarterly, last 3 years for yearly)
  const defaultDiffsPeriods = ['2025Q4', '2025Q3', '2025Q2', '2025Q1', '2024Q4', '2024Q3', '2024Q2'];
  const defaultYearlyPeriods = ['2025Y', '2024Y', '2023Y'];

  // Initialize form with existing config
  useEffect(() => {
    if (open) {
      if (initialConfig) {
        // Determine mode (backward compat: undefined mode = 'current')
        const configMode = initialConfig.mode || 'current';
        setMode(configMode);

        if (configMode === 'historical') {
          // Historical mode
          setMetric(initialConfig.metric || 'average_addons');
          setSelectedPeriods(new Set(initialConfig.selectedPeriods || []));
          setChartType(initialConfig.chartType || 'bar');
          setTitle(initialConfig.title || '');
        } else if (configMode === 'diffs') {
          // Diffs mode
          setDiffsBreakdown(initialConfig.diffsBreakdown || 'event_type');
          setDiffsSelectedPeriods(new Set(initialConfig.diffsSelectedPeriods || []));
          setDiffsEventCategory(initialConfig.diffsEventCategory || 'all');
          setDiffsRequireAllPeriods(initialConfig.diffsRequireAllPeriods !== false); // Default true
          setDiffsShiftQuarters(initialConfig.diffsShiftQuarters !== false); // Default true
          setDiffsCountMode(initialConfig.diffsCountMode || 'unique'); // Default unique
          setDiffsShowTrendLine(initialConfig.diffsShowTrendLine !== false); // Default true
          setDiffsChartMode(initialConfig.diffsChartMode || 'periods'); // Default periods
          setDiffsPeriodGranularity(initialConfig.diffsPeriodGranularity || 'quarterly'); // Default quarterly
          setDiffsGroupByCategory(initialConfig.diffsGroupByCategory || false); // Default false
          setChartType(initialConfig.chartType || 'bar');
          setTitle(initialConfig.title || '');
        } else {
          // Current mode
          let breakdownValue: BreakdownType = 'category';
          if (initialConfig.breakdown) {
            breakdownValue = initialConfig.breakdown;
          } else if ((initialConfig as any).breakdown_by && Array.isArray((initialConfig as any).breakdown_by)) {
            breakdownValue = (initialConfig as any).breakdown_by[0] || 'category';
          }

          setBreakdown(breakdownValue);
          setChartType(initialConfig.chartType || 'pie');
          setTitle(initialConfig.title || '');
        }
      } else {
        // Reset to defaults
        setMode('current');
        setBreakdown('category');
        setChartType('pie');
        setTitle('');
        setMetric('average_addons');
        setSelectedPeriods(new Set());
        setDiffsBreakdown('event_type');
        setDiffsSelectedPeriods(new Set(defaultDiffsPeriods));
        setDiffsEventCategory('all');
        setDiffsRequireAllPeriods(true);
        setDiffsShiftQuarters(true);
        setDiffsCountMode('unique');
        setDiffsShowTrendLine(true);
        setDiffsChartMode('periods');
        setDiffsPeriodGranularity('quarterly');
      }
    }
  }, [initialConfig, open]);

  // Period selection handlers
  const handlePeriodToggle = (period: string) => {
    const newPeriods = new Set(selectedPeriods);
    if (newPeriods.has(period)) {
      newPeriods.delete(period);
    } else {
      newPeriods.add(period);
    }
    setSelectedPeriods(newPeriods);
  };

  const handleSelectAll = () => {
    setSelectedPeriods(new Set(availableQuarters));
  };

  const handleClearAll = () => {
    setSelectedPeriods(new Set());
  };

  // Diffs period selection handlers
  const handleDiffsPeriodToggle = (period: string) => {
    const newPeriods = new Set(diffsSelectedPeriods);
    if (newPeriods.has(period)) {
      newPeriods.delete(period);
    } else {
      newPeriods.add(period);
    }
    setDiffsSelectedPeriods(newPeriods);
  };

  const handleDiffsSelectAll = () => {
    setDiffsSelectedPeriods(new Set(availableDiffsPeriods));
  };

  const handleDiffsClearAll = () => {
    setDiffsSelectedPeriods(new Set());
  };

  // Handle granularity change - clear selected periods and set defaults
  const handleGranularityChange = (granularity: PeriodGranularity) => {
    setDiffsPeriodGranularity(granularity);
    // Set appropriate default periods for the new granularity
    if (granularity === 'yearly') {
      setDiffsSelectedPeriods(new Set(defaultYearlyPeriods));
    } else {
      setDiffsSelectedPeriods(new Set(defaultDiffsPeriods));
    }
  };

  const handleSave = () => {
    if (mode === 'historical') {
      const config: GraphConfig = {
        mode: 'historical',
        metric,
        selectedPeriods: Array.from(selectedPeriods).sort((a, b) => b.localeCompare(a)), // Newest first
        chartType,
        title: title.trim() || undefined
      };
      onSave(config);
    } else if (mode === 'diffs') {
      const config: GraphConfig = {
        mode: 'diffs',
        diffsBreakdown,
        diffsSelectedPeriods: Array.from(diffsSelectedPeriods).sort((a, b) => a.localeCompare(b)), // Oldest first for chronological display
        diffsEventCategory: (diffsBreakdown === 'event_type' || diffsChartMode === 'categories') ? diffsEventCategory : undefined,
        diffsRequireAllPeriods,
        diffsShiftQuarters: diffsPeriodGranularity === 'quarterly' ? diffsShiftQuarters : undefined, // Only relevant for quarterly
        diffsCountMode,
        diffsShowTrendLine,
        diffsChartMode: (diffsBreakdown === 'category' || diffsBreakdown === 'event_type' || diffsBreakdown === 'event_category') ? diffsChartMode : undefined,
        diffsPeriodGranularity,
        diffsGroupByCategory: (diffsChartMode === 'categories') ? diffsGroupByCategory : undefined,
        chartType,
        title: title.trim() || undefined
      };
      onSave(config);
    } else {
      const config: GraphConfig = {
        mode: 'current',
        breakdown,
        chartType,
        title: title.trim() || undefined
      };
      onSave(config);
    }
    onOpenChange(false);
  };

  // Validation
  const canSave = mode === 'historical'
    ? selectedPeriods.size >= 2 && metric
    : mode === 'diffs'
    ? diffsSelectedPeriods.size >= 1 && diffsBreakdown
    : breakdown && chartType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Analytics Graph</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Mode Selection */}
          <div className="space-y-3">
            <Label>Analysis Type</Label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setMode('current')}
                className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                  mode === 'current'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <BarChart2 className="h-8 w-8 text-gray-600" />
                <div className="font-medium">Current</div>
                <div className="text-sm text-gray-500 text-left">
                  Breakdown view of latest data
                </div>
              </button>

              <button
                onClick={() => setMode('historical')}
                className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                  mode === 'historical'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <TrendingUp className="h-8 w-8 text-gray-600" />
                <div className="font-medium">Historical</div>
                <div className="text-sm text-gray-500 text-left">
                  Trend analysis across periods
                </div>
              </button>

              <button
                onClick={() => setMode('diffs')}
                className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                  mode === 'diffs'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <GitCompare className="h-8 w-8 text-gray-600" />
                <div className="font-medium">Diffs</div>
                <div className="text-sm text-gray-500 text-left">
                  Pricing changes over time
                </div>
              </button>
            </div>
          </div>

          {/* Current Mode Configuration */}
          {mode === 'current' && (
            <>
              {/* Breakdown Selection */}
              <div className="space-y-3">
                <Label>Breakdown By</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBreakdown('category')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      breakdown === 'category'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Category</div>
                    <div className="text-sm text-gray-500 text-left">
                      {BREAKDOWN_METADATA.category.description}
                    </div>
                  </button>

                  <button
                    onClick={() => setBreakdown('employees')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      breakdown === 'employees'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Employee Count</div>
                    <div className="text-sm text-gray-500 text-left">
                      {BREAKDOWN_METADATA.employees.description}
                    </div>
                  </button>
                </div>
              </div>

              {/* Chart Type Selection */}
              <div className="space-y-3">
                <Label>Chart Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setChartType('pie')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'pie'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <PieChartIcon className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Pie Chart</div>
                    <div className="text-xs text-gray-500">Show proportions</div>
                  </button>

                  <button
                    onClick={() => setChartType('bar')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'bar'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <BarChart3 className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Bar Chart</div>
                    <div className="text-xs text-gray-500">Compare values</div>
                  </button>
                </div>
              </div>

              {/* Optional Title */}
              <div className="space-y-2">
                <Label htmlFor="graph-title">Custom Title (optional)</Label>
                <Input
                  id="graph-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={BREAKDOWN_METADATA[breakdown]?.label || 'Graph Title'}
                />
                <p className="text-xs text-gray-500">
                  Leave empty to use default: "{BREAKDOWN_METADATA[breakdown]?.label || 'Graph Title'}"
                </p>
              </div>
            </>
          )}

          {/* Historical Mode Configuration */}
          {mode === 'historical' && (
            <>
              {/* Metric Selection */}
              <div className="space-y-2">
                <Label htmlFor="metric-select">Metric</Label>
                <Select value={metric} onValueChange={(value) => setMetric(value as HistoricalMetricType)}>
                  <SelectTrigger id="metric-select">
                    <SelectValue placeholder="Select metric" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="average_addons">
                      {HISTORICAL_METRIC_METADATA.average_addons.label}
                    </SelectItem>
                    <SelectItem value="average_modalities">
                      {HISTORICAL_METRIC_METADATA.average_modalities.label}
                    </SelectItem>
                    <SelectItem value="average_thresholds">
                      {HISTORICAL_METRIC_METADATA.average_thresholds.label}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {HISTORICAL_METRIC_METADATA[metric]?.description}
                </p>
              </div>

              {/* Period Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Time Periods (minimum 2)</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleClearAll}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                  {availableQuarters.map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => handlePeriodToggle(period)}
                      className={`px-3 py-2 text-sm rounded border-2 transition-all ${
                        selectedPeriods.has(period)
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={selectedPeriods.size < 2 ? 'text-red-600' : 'text-gray-500'}>
                    {selectedPeriods.size} period{selectedPeriods.size !== 1 ? 's' : ''} selected
                  </span>
                  {selectedPeriods.size < 2 && (
                    <span className="text-red-600">Please select at least 2 periods</span>
                  )}
                </div>
              </div>

              {/* Chart Type Selection */}
              <div className="space-y-3">
                <Label>Chart Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setChartType('bar')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'bar'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <BarChart3 className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Bar Chart</div>
                    <div className="text-xs text-gray-500">Compare periods</div>
                  </button>

                  <button
                    onClick={() => setChartType('pie')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'pie'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <PieChartIcon className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Line Chart</div>
                    <div className="text-xs text-gray-500">Show trend</div>
                  </button>
                </div>
              </div>

              {/* Optional Title */}
              <div className="space-y-2">
                <Label htmlFor="graph-title-historical">Custom Title (optional)</Label>
                <Input
                  id="graph-title-historical"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={HISTORICAL_METRIC_METADATA[metric]?.label || 'Graph Title'}
                />
                <p className="text-xs text-gray-500">
                  Leave empty to use default: "{HISTORICAL_METRIC_METADATA[metric]?.label || 'Graph Title'}"
                </p>
              </div>
            </>
          )}

          {/* Diffs Mode Configuration */}
          {mode === 'diffs' && (
            <>
              {/* Breakdown Selection */}
              <div className="space-y-3">
                <Label>Breakdown By</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setDiffsBreakdown('event_type')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      diffsBreakdown === 'event_type'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Event Type</div>
                    <div className="text-sm text-gray-500 text-left">
                      {DIFFS_BREAKDOWN_METADATA.event_type.description}
                    </div>
                  </button>

                  <button
                    onClick={() => setDiffsBreakdown('category')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      diffsBreakdown === 'category'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Category</div>
                    <div className="text-sm text-gray-500 text-left">
                      {DIFFS_BREAKDOWN_METADATA.category.description}
                    </div>
                  </button>

                  <button
                    onClick={() => setDiffsBreakdown('employees')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      diffsBreakdown === 'employees'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Company Size</div>
                    <div className="text-sm text-gray-500 text-left">
                      {DIFFS_BREAKDOWN_METADATA.employees.description}
                    </div>
                  </button>

                  <button
                    onClick={() => setDiffsBreakdown('event_category')}
                    className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                      diffsBreakdown === 'event_category'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Event Category</div>
                    <div className="text-sm text-gray-500 text-left">
                      {DIFFS_BREAKDOWN_METADATA.event_category.description}
                    </div>
                  </button>
                </div>
              </div>

              {/* Event Category Filter (when breakdown is event_type OR chart mode is categories) */}
              {(diffsBreakdown === 'event_type' || diffsChartMode === 'categories') && (
                <div className="space-y-3">
                  <Label>Event Category</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(EVENT_CATEGORY_METADATA) as EventCategoryFilter[]).map((category) => (
                      <button
                        key={category}
                        onClick={() => setDiffsEventCategory(category)}
                        className={`flex flex-col items-start gap-1 p-3 border-2 rounded-lg transition-all ${
                          diffsEventCategory === category
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{EVENT_CATEGORY_METADATA[category].label}</div>
                        <div className="text-xs text-gray-500 text-left">
                          {EVENT_CATEGORY_METADATA[category].description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Period Granularity Toggle */}
              <div className="space-y-3">
                <Label>Period Granularity</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleGranularityChange('quarterly')}
                    className={`flex flex-col items-start gap-2 p-3 border-2 rounded-lg transition-all ${
                      diffsPeriodGranularity === 'quarterly'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm">Quarterly</div>
                    <div className="text-xs text-gray-500 text-left">
                      e.g. 2025Q4, 2025Q3, 2025Q2
                    </div>
                  </button>

                  <button
                    onClick={() => handleGranularityChange('yearly')}
                    className={`flex flex-col items-start gap-2 p-3 border-2 rounded-lg transition-all ${
                      diffsPeriodGranularity === 'yearly'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm">Yearly</div>
                    <div className="text-xs text-gray-500 text-left">
                      e.g. 2025, 2024, 2023
                    </div>
                  </button>
                </div>
              </div>

              {/* Period Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Time Periods (minimum 1)</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDiffsSelectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDiffsClearAll}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                  {availableDiffsPeriods.map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => handleDiffsPeriodToggle(period)}
                      className={`px-3 py-2 text-sm rounded border-2 transition-all ${
                        diffsSelectedPeriods.has(period)
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      {/* Display without Y suffix for yearly periods */}
                      {diffsPeriodGranularity === 'yearly' ? period.replace('Y', '') : period}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={diffsSelectedPeriods.size < 1 ? 'text-red-600' : 'text-gray-500'}>
                    {diffsSelectedPeriods.size} period{diffsSelectedPeriods.size !== 1 ? 's' : ''} selected
                  </span>
                  {diffsSelectedPeriods.size < 1 && (
                    <span className="text-red-600">Please select at least 1 period</span>
                  )}
                </div>
              </div>

              {/* Require All Periods Toggle (only show when 2+ periods selected) */}
              {diffsSelectedPeriods.size >= 2 && (
                <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div>
                    <div className="font-medium text-sm">Require all periods</div>
                    <div className="text-xs text-gray-500">
                      Only include pages with diffs in all selected periods
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDiffsRequireAllPeriods(!diffsRequireAllPeriods)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      diffsRequireAllPeriods ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        diffsRequireAllPeriods ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Shift Quarters Toggle (only show for quarterly granularity) */}
              {diffsPeriodGranularity === 'quarterly' && (
                <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div>
                    <div className="font-medium text-sm">Shift quarters</div>
                    <div className="text-xs text-gray-500">
                      Display quarters shifted back by 1 (e.g. 2026Q1 → 2025Q4)
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDiffsShiftQuarters(!diffsShiftQuarters)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      diffsShiftQuarters ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        diffsShiftQuarters ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Count Mode Toggle */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div>
                  <div className="font-medium text-sm">Count unique pages</div>
                  <div className="text-xs text-gray-500">
                    {diffsCountMode === 'unique'
                      ? 'Count one entry per page per period (recommended)'
                      : 'Count all events (a page with 3 price increases counts as 3)'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDiffsCountMode(diffsCountMode === 'unique' ? 'all' : 'unique')}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    diffsCountMode === 'unique' ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      diffsCountMode === 'unique' ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Show Trend Line Toggle */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div>
                  <div className="font-medium text-sm">Show trend line</div>
                  <div className="text-xs text-gray-500">
                    {diffsShowTrendLine
                      ? 'Display the total trend line over stacked bars'
                      : 'Only show stacked bars without trend line'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDiffsShowTrendLine(!diffsShowTrendLine)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    diffsShowTrendLine ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      diffsShowTrendLine ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Group by Category Toggle (only show when chart mode is categories) */}
              {diffsChartMode === 'categories' && (
                <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div>
                    <div className="font-medium text-sm">Group by event category</div>
                    <div className="text-xs text-gray-500">
                      {diffsGroupByCategory
                        ? 'Show 3 category groups (pricing/packaging/product) instead of 24 event types'
                        : 'Show all individual event types (up to 24 different colors)'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDiffsGroupByCategory(!diffsGroupByCategory)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      diffsGroupByCategory ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        diffsGroupByCategory ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Chart Mode Toggle (when breakdown is category, event_type, or event_category) */}
              {(diffsBreakdown === 'category' || diffsBreakdown === 'event_type' || diffsBreakdown === 'event_category') && (
                <div className="space-y-3">
                  <Label>X-Axis Display</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setDiffsChartMode('periods')}
                      className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                        diffsChartMode === 'periods'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">Periods on X-axis</div>
                      <div className="text-sm text-gray-500 text-left">
                        {diffsBreakdown === 'category'
                          ? 'Show quarters on X-axis, stacked by category'
                          : diffsBreakdown === 'event_category'
                          ? 'Show quarters on X-axis, stacked by event category'
                          : 'Show quarters on X-axis, stacked by event type'}
                      </div>
                    </button>

                    {diffsBreakdown === 'category' && (
                      <button
                        onClick={() => setDiffsChartMode('categories')}
                        className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                          diffsChartMode === 'categories'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium">Categories on X-axis</div>
                        <div className="text-sm text-gray-500 text-left">
                          Show categories on X-axis, stacked by event type
                        </div>
                      </button>
                    )}

                    {diffsBreakdown === 'event_type' && (
                      <button
                        onClick={() => setDiffsChartMode('events')}
                        className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                          diffsChartMode === 'events'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium">Events on X-axis</div>
                        <div className="text-sm text-gray-500 text-left">
                          Show event types on X-axis, grouped by period
                        </div>
                      </button>
                    )}

                    {diffsBreakdown === 'event_category' && (
                      <button
                        onClick={() => setDiffsChartMode('categories')}
                        className={`flex flex-col items-start gap-2 p-4 border-2 rounded-lg transition-all ${
                          diffsChartMode === 'categories'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium">Event Categories on X-axis</div>
                        <div className="text-sm text-gray-500 text-left">
                          Show event categories on X-axis, grouped by period
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Chart Type Selection */}
              <div className="space-y-3">
                <Label>Chart Type</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setChartType('bar')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'bar'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <BarChart3 className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Stacked Bar</div>
                    <div className="text-xs text-gray-500">Compare totals</div>
                  </button>

                  <button
                    onClick={() => setChartType('grouped')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'grouped'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <BarChart2 className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Grouped Bar</div>
                    <div className="text-xs text-gray-500">Side by side</div>
                  </button>

                  <button
                    onClick={() => setChartType('pie')}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      chartType === 'pie'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <TrendingUp className="h-8 w-8 text-gray-600" />
                    <div className="font-medium">Line Chart</div>
                    <div className="text-xs text-gray-500">Show trend</div>
                  </button>
                </div>
              </div>

              {/* Optional Title */}
              <div className="space-y-2">
                <Label htmlFor="graph-title-diffs">Custom Title (optional)</Label>
                <Input
                  id="graph-title-diffs"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={DIFFS_BREAKDOWN_METADATA[diffsBreakdown]?.label || 'Pricing Changes'}
                />
                <p className="text-xs text-gray-500">
                  Leave empty to use default: "{DIFFS_BREAKDOWN_METADATA[diffsBreakdown]?.label || 'Pricing Changes'}"
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save Graph
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
